import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import Button from '@atlaskit/button/new';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { cssMap } from '@atlaskit/css';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { PAGE_SIZE_OPTIONS } from '../../hooks/useContentMetrics.ts';
import type { CurrentRange, PaginationInfo } from '../../hooks/useContentMetrics.ts';

const styles = cssMap({
	footer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginBlockStart: token('space.100'),
		paddingInline: token('space.200'),
	},
	leftSection: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
	rightSection: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
});

const i18n = defineMessages({
	previousPage: {
		id: 'performance-insights.content-metrics.pagination.previous-page.non-final',
		defaultMessage: 'Previous',
		description:
			'Button label to go to the previous page of the content metrics table. Located in the pagination footer bar below the table.',
	},
	nextPage: {
		id: 'performance-insights.content-metrics.pagination.next-page.non-final',
		defaultMessage: 'Next',
		description:
			'Button label to go to the next page of the content metrics table. Located in the pagination footer bar below the table.',
	},
	previousPageAriaLabel: {
		id: 'performance-insights.content-metrics.pagination.previous-page-aria-label.non-final',
		defaultMessage: 'Previous page',
		description:
			'Accessible label for the Previous page button in the pagination footer. Used by screen readers to announce the button purpose.',
	},
	nextPageAriaLabel: {
		id: 'performance-insights.content-metrics.pagination.next-page-aria-label.non-final',
		defaultMessage: 'Next page',
		description:
			'Accessible label for the Next page button in the pagination footer. Used by screen readers to announce the button purpose.',
	},
	resultsRange: {
		id: 'performance-insights.content-metrics.pagination.results-range.non-final',
		defaultMessage: 'Results {start} - {end}',
		description:
			'Shows the range of results currently displayed in the content metrics table (e.g. Results 1 - 25). {start} is the first result number, {end} is the last.',
	},
	resultsPerPage: {
		id: 'performance-insights.content-metrics.pagination.results-per-page.non-final',
		defaultMessage: 'Results per page:',
		description:
			'Label for the dropdown that controls how many results are shown per page in the content metrics table.',
	},
});

export interface TablePaginationFooterProps {
	paginationInfo: PaginationInfo;
	currentRange: CurrentRange;
	pageSize: number;
	loading: boolean;
	onNextPage: () => void;
	onPreviousPage: () => void;
	onPageSizeChange: (size: number) => void;
}

export const TablePaginationFooter = ({
	paginationInfo,
	currentRange,
	pageSize,
	loading,
	onNextPage,
	onPreviousPage,
	onPageSizeChange,
}: TablePaginationFooterProps) => {
	const { formatMessage } = useIntl();

	const isPreviousDisabled = !paginationInfo.hasPreviousPage || loading;
	const isNextDisabled = !paginationInfo.hasNextPage || loading;

	const pageSizeHandlers = useMemo(
		() =>
			Object.fromEntries(
				PAGE_SIZE_OPTIONS.map((size) => [size, () => onPageSizeChange(size)]),
			) as Record<number, () => void>,
		[onPageSizeChange],
	);

	return (
		<Box xcss={styles.footer}>
			<Box xcss={styles.leftSection}>
				<Button
					spacing="compact"
					isDisabled={isPreviousDisabled}
					onClick={onPreviousPage}
					aria-label={formatMessage(i18n.previousPageAriaLabel)}
					iconBefore={(iconProps) => <ChevronLeftIcon {...iconProps} label="" />}
				>
					{formatMessage(i18n.previousPage)}
				</Button>
				<Button
					spacing="compact"
					isDisabled={isNextDisabled}
					onClick={onNextPage}
					aria-label={formatMessage(i18n.nextPageAriaLabel)}
					iconAfter={(iconProps) => <ChevronRightIcon {...iconProps} label="" />}
				>
					{formatMessage(i18n.nextPage)}
				</Button>
				<Text>
					{formatMessage(i18n.resultsRange, {
						start: currentRange.start,
						end: currentRange.end,
					})}
				</Text>
			</Box>
			<Box xcss={styles.rightSection}>
				<Inline space="space.100" alignBlock="center">
					<Text>{formatMessage(i18n.resultsPerPage)}</Text>
					<DropdownMenu<HTMLButtonElement>
						trigger={({ triggerRef, ...props }) => (
							<Button
								ref={triggerRef}
								{...props}
								spacing="compact"
								iconAfter={(iconProps) => <ChevronDownIcon {...iconProps} label="" />}
							>
								{String(pageSize)}
							</Button>
						)}
						shouldRenderToParent
					>
						<DropdownItemGroup>
							{PAGE_SIZE_OPTIONS.map((size) => (
								<DropdownItem
									key={size}
									onClick={pageSizeHandlers[size]}
									isSelected={size === pageSize}
									isDisabled={size === pageSize || loading}
								>
									{String(size)}
								</DropdownItem>
							))}
						</DropdownItemGroup>
					</DropdownMenu>
				</Inline>
			</Box>
		</Box>
	);
};
